import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import { getApiBaseUrl } from '../../../core/config/api';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';

interface SystemMaintenanceProps {
  /** Rendu dans le shell du panel v1 (sans Navbar / fond plein écran). */
  embedded?: boolean;
}

type MaintenanceStatus = {
  enabled: boolean;
  message: string | null;
  scheduled_at: string | null;
  scheduled_message: string | null;
};

const SystemMaintenance: React.FC<SystemMaintenanceProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [status, setStatus] = useState<MaintenanceStatus>({
    enabled: false,
    message: null,
    scheduled_at: null,
    scheduled_message: null,
  });
  const [messageInput, setMessageInput] = useState('');
  const [scheduledAtInput, setScheduledAtInput] = useState('');
  const [scheduledMessageInput, setScheduledMessageInput] = useState('');

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/admin/maintenance/status`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.data) {
        throw new Error(data?.error || 'Impossible de charger la maintenance.');
      }
      const nextStatus: MaintenanceStatus = {
        enabled: data.data.enabled === true || Number(data.data.enabled) === 1,
        message: data.data.message || null,
        scheduled_at: data.data.scheduled_at || null,
        scheduled_message: data.data.scheduled_message || null,
      };
      setStatus(nextStatus);
      setMessageInput(nextStatus.message || '');
      setScheduledAtInput(
        nextStatus.scheduled_at
          ? new Date(nextStatus.scheduled_at).toISOString().slice(0, 16)
          : ''
      );
      setScheduledMessageInput(nextStatus.scheduled_message || '');
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  if (!currentUser) {
    return null;
  }

  if (currentUser.roles_id !== 1) {
    const denied = (
      <div className="rounded-xl border border-red-500/40 bg-red-950/30 p-6 text-white">
        <h1 className="text-xl font-bold text-red-200">Accès refusé</h1>
        <p className="mt-2 text-sm text-red-100">Cette page est réservée au Founder.</p>
        <button
          type="button"
          onClick={() => navigate('/panel')}
          className="mt-4 rounded-lg border border-red-400/40 bg-red-700/30 px-4 py-2 text-sm font-semibold"
        >
          Retour panel
        </button>
      </div>
    );
    if (embedded) {
      return <div className="max-w-4xl">{denied}</div>;
    }
    return (
      <div className="relative min-h-screen overflow-hidden text-white">
        <BackgroundImage />
        <ParticlesBackground />
        <Navbar />
        <div className="relative z-10 mx-auto max-w-4xl px-6 pt-24">{denied}</div>
      </div>
    );
  }

  const saveStatus = async (payload: Partial<MaintenanceStatus>) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/admin/maintenance/status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.data) {
        throw new Error(data?.error || 'Impossible de sauvegarder la maintenance.');
      }
      const nextStatus: MaintenanceStatus = {
        enabled: data.data.enabled === true || Number(data.data.enabled) === 1,
        message: data.data.message || null,
        scheduled_at: data.data.scheduled_at || null,
        scheduled_message: data.data.scheduled_message || null,
      };
      setStatus(nextStatus);
      setSuccess('Maintenance mise à jour avec succès.');
      setMessageInput(nextStatus.message || '');
      setScheduledAtInput(
        nextStatus.scheduled_at ? new Date(nextStatus.scheduled_at).toISOString().slice(0, 16) : ''
      );
      setScheduledMessageInput(nextStatus.scheduled_message || '');
    } catch (e: any) {
      setError(e?.message || 'Erreur de sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const backTarget = embedded ? '/panel/cm' : '/panel';
  const backLabel = embedded ? 'Espace CM' : 'Retour panel';

  const body = (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className={`font-bold text-white ${embedded ? 'text-xl' : 'text-2xl'}`}>Maintenance système</h1>
          <p className={`mt-1 text-slate-400 ${embedded ? 'text-xs' : 'text-sm'}`}>
            Gestion privée de la maintenance (panel CM).
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(backTarget)}
          className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
        >
          {backLabel}
        </button>
      </div>

      <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-5 backdrop-blur-sm">
          {loading ? (
            <p className="text-sm text-gray-400">Chargement du statut...</p>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-3">
                <div>
                  <p className="text-sm font-semibold">Maintenance active</p>
                  <p className="text-xs text-gray-500">
                    Active immédiatement le mode maintenance côté public.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={status.enabled}
                  onClick={() => void saveStatus({ enabled: !status.enabled })}
                  disabled={saving}
                  className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-60 ${
                    status.enabled ? 'bg-orange-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
                      status.enabled ? 'left-6' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-400">
                  Message maintenance active
                </label>
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Ex: Maintenance serveur en cours, retour prévu à 14h30."
                  className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#3d4fdb]/60"
                />
                <button
                  type="button"
                  onClick={() => void saveStatus({ message: messageInput || null })}
                  disabled={saving}
                  className="mt-2 rounded-lg border border-orange-500/50 bg-orange-600/20 px-3 py-1.5 text-xs font-semibold text-orange-100 disabled:opacity-60"
                >
                  Enregistrer le message
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-400">
                    Maintenance programmée (date/heure)
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAtInput}
                    onChange={(e) => setScheduledAtInput(e.target.value)}
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-400">
                    Message maintenance programmée
                  </label>
                  <input
                    type="text"
                    value={scheduledMessageInput}
                    onChange={(e) => setScheduledMessageInput(e.target.value)}
                    placeholder="Ex: Maintenance prévue ce soir."
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm outline-none focus:border-amber-500"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void saveStatus({
                      scheduled_at: scheduledAtInput ? new Date(scheduledAtInput).toISOString() : null,
                      scheduled_message: scheduledMessageInput || null,
                    })
                  }
                  disabled={saving}
                  className="rounded-lg border border-amber-500/50 bg-amber-600/20 px-3 py-1.5 text-xs font-semibold text-amber-100 disabled:opacity-60"
                >
                  Enregistrer la programmation
                </button>
                <button
                  type="button"
                  onClick={() => void saveStatus({ scheduled_at: null, scheduled_message: null })}
                  disabled={saving}
                  className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-black/45 disabled:opacity-40 transition-colors"
                >
                  Annuler la programmation
                </button>
              </div>
            </div>
          )}
          {error ? <p className="mt-4 text-sm text-red-300">{error}</p> : null}
          {success ? <p className="mt-4 text-sm text-emerald-300">{success}</p> : null}
        </div>
    </>
  );

  if (embedded) {
    return <div className="mx-auto max-w-4xl text-slate-100">{body}</div>;
  }

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 mx-auto max-w-4xl px-6 pb-10 pt-24">{body}</div>
    </div>
  );
};

export default SystemMaintenance;
